import { NgClass, NgTemplateOutlet } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
  IsActiveMatchOptions,
  RouterLink,
  RouterLinkActive,
} from "@angular/router";
import { FuseNavigationService } from "@fuse/components/navigation/navigation.service";
import { FuseNavigationItem } from "@fuse/components/navigation/navigation.types";
import { FuseVerticalNavigationComponent } from "@fuse/components/navigation/vertical/vertical.component";
import { FuseConfig, FuseConfigService } from "@fuse/services/config";
import { FuseUtilsService } from "@fuse/services/utils/utils.service";
import { Subject, takeUntil } from "rxjs";

import { TranslocoModule, TranslocoService } from "@jsverse/transloco";

import { BreadcrumbService } from "app/core/services/breadcrumb.service";
import { FixedIconSizeRobustDirective } from "app/core/directives/fixed-icon-size-robust.directive";

@Component({
  selector: "fuse-vertical-navigation-basic-item",
  templateUrl: "./basic.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    RouterLink,
    RouterLinkActive,
    MatTooltipModule,
    NgTemplateOutlet,
    MatIconModule,
    TranslocoModule,
    FixedIconSizeRobustDirective,
  ],
})
export class FuseVerticalNavigationBasicItemComponent
  implements OnInit, OnDestroy
{
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _fuseNavigationService = inject(FuseNavigationService);
  private readonly _fuseUtilsService = inject(FuseUtilsService);
  private readonly _breadCrumbService = inject(BreadcrumbService);
  private readonly _translocoService = inject(TranslocoService);

  @Input() item: FuseNavigationItem;
  @Input() name: string;

  // Set the equivalent of {exact: false} as default for active match options.
  // We are not assigning the item.isActiveMatchOptions directly to the
  // [routerLinkActiveOptions] because if it's "undefined" initially, the router
  // will throw an error and stop working.
  isActiveMatchOptions: IsActiveMatchOptions =
    this._fuseUtilsService.subsetMatchOptions;

  private _fuseVerticalNavigationComponent: FuseVerticalNavigationComponent;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  config: FuseConfig;
  constructor(private _fuseConfigService: FuseConfigService) {}
  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this._fuseConfigService.config$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config: FuseConfig) => {
        // Store the config
        this.config = config;
      });
    // Set the "isActiveMatchOptions" either from item's
    // "isActiveMatchOptions" or the equivalent form of
    // item's "exactMatch" option
    this.isActiveMatchOptions =
      (this.item.isActiveMatchOptions ?? this.item.exactMatch)
        ? this._fuseUtilsService.exactMatchOptions
        : this._fuseUtilsService.subsetMatchOptions;

    // Get the parent navigation component
    this._fuseVerticalNavigationComponent =
      this._fuseNavigationService.getComponent(this.name);

    // Mark for check
    this._changeDetectorRef.markForCheck();

    // Subscribe to onRefreshed on the navigation component
    this._fuseVerticalNavigationComponent.onRefreshed
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        // Mark for check
        this._changeDetectorRef.markForCheck();
      });

    // Add when we have dark and light mode
    // setTimeout(() => {
    // const mainNavigationComponent =
    // this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
    // "mainNavigation"
    // );

    // if (mainNavigationComponent) {
    // const mainNavigation = mainNavigationComponent.navigation;
    // const menuItem = this._fuseNavigationService.getItem(
    //   "mode",
    //   mainNavigation
    // );
    // menuItem.title =
    //   this.config.scheme == "dark" ? "Light Mode" : "Dark Mode";
    // menuItem.icon =
    //   this.config.scheme == "dark"
    //     ? "heroicons_solid:sun"
    //     : "heroicons_solid:moon";
    // menuItem.function = () => {
    //   this._fuseConfigService.config = {
    //     scheme: this.config.scheme == "dark" ? "light" : "dark",
    //   };
    //   menuItem.title =
    //     this.config.scheme == "dark" ? "Light Mode" : "Dark Mode";
    //   menuItem.icon =
    //     this.config.scheme == "dark"
    //       ? "heroicons_solid:sun"
    //       : "heroicons_solid:moon";
    // };
    // mainNavigationComponent.refresh();
    // }
    // });
  }

  handleItemOnClick(item: FuseNavigationItem) {
    this._breadCrumbService.menuItems = [
      {
        label: "Home",
        routerLink: "/",
      },
    ];
    let parts = item.link.split("/");
    parts.forEach((part, index) => {
      if (part) {
        this._breadCrumbService.menuItems.push({
          label: this._translocoService.translate("breadcrumb." + part),
          routerLink: index == parts.length - 1 ? item.link : "",
        });
      }
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
